// Cross tab logout flag.
//
// redux-persist keeps this app's session state in sessionStorage, which is per tab,
// so a sign out in one tab leaves the others believing they are still authenticated.
// localStorage is shared across every tab of the same origin, so a flag written here
// is visible everywhere and survives a reload.
//
// The flag stores the time of the logout rather than a boolean. Tokens issued before
// that time belong to the dead session, tokens issued after it belong to a new login.
// Without the timestamp there is no way to tell a fresh sign in from stale cached
// tokens, and the app would log the user straight back out again.

const LOGOUT_KEY = 'appLoggedOutAt';

// set on an explicit sign out, or when the refresh token can no longer be renewed
const setLoggedOut = () => {
  try {
    window.localStorage.setItem(LOGOUT_KEY, Date.now().toString());
  } catch (err) {
    console.log(err);
  }
};

const clearLoggedOut = () => {
  try {
    window.localStorage.removeItem(LOGOUT_KEY);
  } catch (err) {
    console.log(err);
  }
};

// epoch ms of the last logout, or null when there is none
const getLoggedOutAt = () => {
  try {
    const value = window.localStorage.getItem(LOGOUT_KEY);
    return value === null ? null : Number(value);
  } catch (err) {
    console.log(err);
    return null;
  }
};

const isLoggedOut = () => getLoggedOutAt() !== null;

// true when the token was issued before the logout, i.e. it belongs to the dead session.
// iat is in seconds, the stored value is in milliseconds.
const isTokenStale = (idToken) => {
  const loggedOutAt = getLoggedOutAt();
  if (loggedOutAt === null) {
    return false;
  }
  const issuedAt = idToken?.iat;
  if (!issuedAt) {
    // no issued at claim - assume stale so we fail closed
    return true;
  }
  return issuedAt * 1000 < loggedOutAt;
};

export { setLoggedOut, clearLoggedOut, isLoggedOut, isTokenStale };
